import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FF, T } from "../lib/constants";

// Oefeningtekening (build 2.3): een tennisbaan met spelers, trainer, ballen, pionnen en
// pijlen (looplijn / balbaan), verdeeld over één of meer stappen.
//
// Opslag (veld `diagram` op een oefening):
//   { frames: [ { note: "", items: [ ... ] } ] }
// Coördinaten zijn in meters op de baan (0,0 = linkerbovenhoek van het dubbelspelveld).
//   speler  { id, t: "p", x, y, n }     trainer { id, t: "c", x, y }
//   bal     { id, t: "b", x, y }        pion    { id, t: "k", x, y }
//   looplijn{ id, t: "r", x1, y1, x2, y2 }   balbaan { id, t: "s", x1, y1, x2, y2 }
//   pionnenrij { id, t: "kr", x1, y1, x2, y2 } (build 2.5: pionnen op gelijke afstand
//               tussen begin- en eindpunt, ongeveer één per meter)

var CW = 10.97, CL = 23.77, NET = CL / 2, SGL = 1.37, SVC = 6.4;
var VB = { x: -2.2, y: -2.6, w: CW + 4.4, h: CL + 5.2 };
var C = {
  surround: "#5E8C61", court: "#4A7A50", line: "#FFFFFF",
  player: "#1A2744", coach: "#B83232", ball: "#E3EC3B", cone: "#F28C28",
  run: "#FFFFFF", shot: "#FFE45C",
};

function uid() { return Math.random().toString(36).slice(2, 9); }
function r1(v) { return Math.round(v * 10) / 10; }
function clampX(x) { return r1(Math.max(VB.x + 0.4, Math.min(VB.x + VB.w - 0.4, x))); }
function clampY(y) { return r1(Math.max(VB.y + 0.4, Math.min(VB.y + VB.h - 0.4, y))); }
function cp(o) { return JSON.parse(JSON.stringify(o)); }
function isArrow(it) { return it.t === "r" || it.t === "s"; }
function isRow(it) { return it.t === "kr"; }
function isLine(it) { return isArrow(it) || isRow(it); }
// Posities van de pionnen in een rij: ongeveer elke meter één, minstens twee.
function rowCones(it) {
  var len = Math.hypot(it.x2 - it.x1, it.y2 - it.y1);
  var n = Math.max(2, Math.round(len / 1.0) + 1);
  var out = [];
  for (var i = 0; i < n; i++) { var t = i / (n - 1); out.push({ x: it.x1 + (it.x2 - it.x1) * t, y: it.y1 + (it.y2 - it.y1) * t }); }
  return out;
}
// Bij afspelen: koppel elk punt (speler, bal, ...) aan een punt uit de vorige stap, zodat
// het daar vandaan schuift. Zelfde id gaat voor; anders het dichtstbijzijnde ongebruikte
// punt van hetzelfde soort (bv. een bal die je in de nieuwe stap opnieuw hebt neergezet).
function matchKeys(points, prev) {
  var keys = {};
  if (!prev) { points.forEach(function (it) { keys[it.id] = it.id; }); return keys; }
  var prevPts = ((prev.items) || []).filter(function (it) { return !isLine(it); });
  var used = {};
  points.forEach(function (it) { if (prevPts.some(function (p) { return p.id === it.id; })) { keys[it.id] = it.id; used[it.id] = true; } });
  points.forEach(function (it) {
    if (keys[it.id]) return;
    var best = null, bd = Infinity;
    prevPts.forEach(function (p) {
      if (used[p.id] || p.t !== it.t || (it.t === "p" && p.n !== it.n)) return;
      var d = Math.hypot(p.x - it.x, p.y - it.y);
      if (d < bd) { bd = d; best = p; }
    });
    if (best) { keys[it.id] = best.id; used[best.id] = true; } else keys[it.id] = it.id;
  });
  return keys;
}

export function hasDiagram(d) {
  return !!(d && d.frames && d.frames.some(function (f) { return (f.items && f.items.length) || (f.note && f.note.trim()); }));
}
function emptyDiagram() { return { frames: [{ note: "", items: [] }] }; }

// ---------------------------------------------------------------- tekenen

function Court() {
  var L = C.line, sw = 0.1;
  return (
    <g>
      <rect x={VB.x} y={VB.y} width={VB.w} height={VB.h} fill={C.surround} />
      <rect x={0} y={0} width={CW} height={CL} fill={C.court} />
      <g stroke={L} strokeWidth={sw} fill="none">
        <rect x={0} y={0} width={CW} height={CL} />
        <line x1={SGL} y1={0} x2={SGL} y2={CL} />
        <line x1={CW - SGL} y1={0} x2={CW - SGL} y2={CL} />
        <line x1={SGL} y1={NET - SVC} x2={CW - SGL} y2={NET - SVC} />
        <line x1={SGL} y1={NET + SVC} x2={CW - SGL} y2={NET + SVC} />
        <line x1={CW / 2} y1={NET - SVC} x2={CW / 2} y2={NET + SVC} />
        <line x1={CW / 2} y1={0} x2={CW / 2} y2={0.3} />
        <line x1={CW / 2} y1={CL} x2={CW / 2} y2={CL - 0.3} />
      </g>
      <line x1={-0.9} y1={NET} x2={CW + 0.9} y2={NET} stroke="#1C1B19" strokeWidth={0.18} />
      <line x1={-0.9} y1={NET} x2={CW + 0.9} y2={NET} stroke="#FFFFFF" strokeWidth={0.05} strokeDasharray="0.2 0.2" />
      <circle cx={-0.9} cy={NET} r={0.16} fill="#1C1B19" />
      <circle cx={CW + 0.9} cy={NET} r={0.16} fill="#1C1B19" />
    </g>
  );
}

function Defs() {
  return (
    <defs>
      <marker id="dg-run" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
        <path d="M0 0 L10 5 L0 10 z" fill={C.run} />
      </marker>
      <marker id="dg-shot" viewBox="0 0 10 10" refX="7" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
        <path d="M0 0 L10 5 L0 10 z" fill={C.shot} />
      </marker>
    </defs>
  );
}

function PointShape({ it }) {
  if (it.t === "p" || it.t === "c") {
    return (
      <g>
        <circle r={0.78} fill={it.t === "p" ? C.player : C.coach} stroke="#fff" strokeWidth={0.12} />
        <text y={0.31} textAnchor="middle" fontSize={0.88} fontWeight={800} fill="#fff" fontFamily="Archivo, system-ui, sans-serif" style={{ pointerEvents: "none", userSelect: "none" }}>
          {it.t === "p" ? (it.n || "") : "T"}
        </text>
      </g>
    );
  }
  if (it.t === "b") return <circle r={0.34} fill={C.ball} stroke="#4A4A10" strokeWidth={0.07} />;
  return <path d="M0 -0.5 L0.45 0.36 L-0.45 0.36 Z" fill={C.cone} stroke="#fff" strokeWidth={0.08} strokeLinejoin="round" />;
}

function ConeShape() {
  return <path d="M0 -0.5 L0.45 0.36 L-0.45 0.36 Z" fill={C.cone} stroke="#fff" strokeWidth={0.08} strokeLinejoin="round" />;
}
function RowShape({ it, ghost }) {
  return (
    <g opacity={ghost ? 0.7 : 1} style={{ pointerEvents: "none" }}>
      {rowCones(it).map(function (p, i) { return <g key={i} transform={"translate(" + p.x + " " + p.y + ")"}><ConeShape /></g>; })}
    </g>
  );
}

// Animaties bij afspelen: lijnen van de vorige stap vervagen, nieuwe lijnen verschijnen
// zodra de spelers en de bal op hun plek zijn.
var ANIM_CSS = "@keyframes dgOut{from{opacity:1}to{opacity:0}}@keyframes dgIn{from{opacity:0}to{opacity:1}}" +
  ".dg-out{animation:dgOut .9s ease-in-out forwards}.dg-in{opacity:0;animation:dgIn .6s ease-out .75s forwards}" +
  "@media (prefers-reduced-motion: reduce){.dg-out{animation:none;opacity:0}.dg-in{animation:none;opacity:1}}";

function ArrowShape({ it, ghost }) {
  var run = it.t === "r";
  return (
    <line x1={it.x1} y1={it.y1} x2={it.x2} y2={it.y2}
      stroke={run ? C.run : C.shot} strokeWidth={0.18} strokeLinecap={run ? "butt" : "round"}
      strokeDasharray={run ? "0.28 0.2" : undefined} markerEnd={"url(#" + (run ? "dg-run" : "dg-shot") + ")"}
      opacity={ghost ? 0.7 : 1} style={{ pointerEvents: "none" }} />
  );
}

// Eén stap van de tekening als SVG. `onItemDown`/`onBgDown` alleen in de bewerkmodus.
function CourtSvg({ frame, prevFrame, stepKey, svgRef, animate, onBgDown, onItemDown, onMove, onUp, draft, showHandles, style }) {
  var items = (frame && frame.items) || [];
  var arrows = items.filter(isLine), points = items.filter(function (it) { return !isLine(it); });
  var editing = !!onBgDown;
  var keys = animate ? matchKeys(points, prevFrame) : null;
  var oldArrows = animate && prevFrame ? (prevFrame.items || []).filter(isArrow) : [];
  return (
    <svg ref={svgRef} viewBox={VB.x + " " + VB.y + " " + VB.w + " " + VB.h} preserveAspectRatio="xMidYMid meet"
      onPointerDown={onBgDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      style={Object.assign({ display: "block", width: "100%", touchAction: editing ? "none" : "auto", userSelect: "none" }, style)}>
      <Defs />
      {animate && <style>{ANIM_CSS}</style>}
      <Court />
      {oldArrows.length > 0 && <g key={"out" + stepKey} className="dg-out">{oldArrows.map(function (it) { return <ArrowShape key={it.id} it={it} />; })}</g>}
      {arrows.filter(isRow).map(function (it) {
        return (
          <g key={it.id}>
            <RowShape it={it} />
            {editing && <line x1={it.x1} y1={it.y1} x2={it.x2} y2={it.y2} stroke="transparent" strokeWidth={1.1} strokeLinecap="round"
              onPointerDown={function (e) { onItemDown(e, it, "line"); }} style={{ cursor: "move" }} />}
            {editing && showHandles && <circle cx={it.x1} cy={it.y1} r={0.42} fill="rgba(255,255,255,.35)" stroke="#fff" strokeWidth={0.06}
              onPointerDown={function (e) { onItemDown(e, it, "a"); }} style={{ cursor: "grab" }} />}
            {editing && showHandles && <circle cx={it.x2} cy={it.y2} r={0.42} fill="rgba(255,255,255,.35)" stroke="#fff" strokeWidth={0.06}
              onPointerDown={function (e) { onItemDown(e, it, "b"); }} style={{ cursor: "grab" }} />}
          </g>
        );
      })}
      <g key={"in" + (animate && prevFrame ? stepKey : "")} className={animate && prevFrame ? "dg-in" : undefined}>
      {arrows.filter(isArrow).map(function (it) {
        return (
          <g key={it.id}>
            <ArrowShape it={it} />
            {editing && <line x1={it.x1} y1={it.y1} x2={it.x2} y2={it.y2} stroke="transparent" strokeWidth={1} strokeLinecap="round"
              onPointerDown={function (e) { onItemDown(e, it, "line"); }} style={{ cursor: "move" }} />}
            {editing && showHandles && <circle cx={it.x1} cy={it.y1} r={0.42} fill="rgba(255,255,255,.35)" stroke="#fff" strokeWidth={0.06}
              onPointerDown={function (e) { onItemDown(e, it, "a"); }} style={{ cursor: "grab" }} />}
            {editing && showHandles && <circle cx={it.x2} cy={it.y2} r={0.42} fill="rgba(255,255,255,.35)" stroke="#fff" strokeWidth={0.06}
              onPointerDown={function (e) { onItemDown(e, it, "b"); }} style={{ cursor: "grab" }} />}
          </g>
        );
      })}
      </g>
      {points.map(function (it) {
        return (
          <g key={keys ? keys[it.id] : it.id} transform={animate ? undefined : "translate(" + it.x + " " + it.y + ")"}
            style={animate ? { transform: "translate(" + it.x + "px," + it.y + "px)", transition: "transform 1s cubic-bezier(.45,.05,.35,1)" } : (editing ? { cursor: "move" } : undefined)}
            onPointerDown={editing ? function (e) { onItemDown(e, it, "p"); } : undefined}>
            {editing && <circle r={1.1} fill="transparent" />}
            <PointShape it={it} />
          </g>
        );
      })}
      {draft && (isRow(draft) ? <RowShape it={draft} ghost /> : <ArrowShape it={draft} ghost />)}
    </svg>
  );
}

// ---------------------------------------------------------------- knoppen

var TOOLS = [
  { key: "move", label: "Verplaats" },
  { key: "p", label: "Speler" },
  { key: "c", label: "Trainer" },
  { key: "b", label: "Bal" },
  { key: "k", label: "Pion" },
  { key: "kr", label: "Pionnenrij" },
  { key: "r", label: "Looplijn" },
  { key: "s", label: "Balbaan" },
  { key: "erase", label: "Gum" },
];

function ToolIcon({ k }) {
  var s = { width: 18, height: 18, display: "block" };
  if (k === "move") return <svg viewBox="0 0 20 20" style={s}><path d="M10 2 L13 5 H11 V9 H15 V7 L18 10 L15 13 V11 H11 V15 H13 L10 18 L7 15 H9 V11 H5 V13 L2 10 L5 7 V9 H9 V5 H7 Z" fill="currentColor" /></svg>;
  if (k === "p") return <svg viewBox="0 0 20 20" style={s}><circle cx="10" cy="10" r="7" fill={C.player} /><text x="10" y="13.5" textAnchor="middle" fontSize="9" fontWeight="800" fill="#fff">1</text></svg>;
  if (k === "c") return <svg viewBox="0 0 20 20" style={s}><circle cx="10" cy="10" r="7" fill={C.coach} /><text x="10" y="13.5" textAnchor="middle" fontSize="9" fontWeight="800" fill="#fff">T</text></svg>;
  if (k === "b") return <svg viewBox="0 0 20 20" style={s}><circle cx="10" cy="10" r="5" fill={C.ball} stroke="#4A4A10" strokeWidth="1" /></svg>;
  if (k === "k") return <svg viewBox="0 0 20 20" style={s}><path d="M10 3 L16 16 H4 Z" fill={C.cone} /></svg>;
  if (k === "kr") return <svg viewBox="0 0 20 20" style={s}><path d="M3 9 L6 15 H0 Z M10 9 L13 15 H7 Z M17 9 L20 15 H14 Z" fill={C.cone} /></svg>;
  if (k === "r") return <svg viewBox="0 0 20 20" style={s}><line x1="3" y1="15" x2="14" y2="5" stroke="#1C1B19" strokeWidth="2" strokeDasharray="1.6 1.4" /><path d="M17 2 L15 9 L10 5 Z" fill="#1C1B19" /></svg>;
  if (k === "s") return <svg viewBox="0 0 20 20" style={s}><line x1="3" y1="15" x2="14" y2="5" stroke="#B89A00" strokeWidth="2.4" /><path d="M17 2 L15 9 L10 5 Z" fill="#B89A00" /></svg>;
  return <svg viewBox="0 0 20 20" style={s}><path d="M4 13 L11 6 L16 11 L11 16 H6 Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><line x1="8" y1="16" x2="17" y2="16" stroke="currentColor" strokeWidth="1.8" /></svg>;
}

function CourtIcon() {
  return (
    <svg viewBox="0 0 14 20" style={{ width: 11, height: 16, display: "block" }}>
      <rect x="1" y="1" width="12" height="18" rx="1" fill={C.court} stroke="#2E5135" strokeWidth="1" />
      <line x1="1" y1="10" x2="13" y2="10" stroke="#fff" strokeWidth="1.2" />
      <line x1="7" y1="5" x2="7" y2="15" stroke="#fff" strokeWidth="0.8" />
    </svg>
  );
}

var btn = { border: "1px solid " + T.line, background: "#fff", borderRadius: 8, padding: "7px 12px", fontSize: 13, fontWeight: 700, color: T.ink, cursor: "pointer", fontFamily: FF };
var btnPrimary = Object.assign({}, btn, { background: T.primary, color: "#fff", border: "1px solid " + T.primary });

function StepTabs({ frames, fi, setFi, onAdd }) {
  if (frames.length < 2 && !onAdd) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      {frames.map(function (f, i) {
        var on = i === fi;
        return <button key={i} onClick={function () { setFi(i); }} style={Object.assign({}, btn, { padding: "5px 11px", fontSize: 12, background: on ? T.primary : "#fff", color: on ? "#fff" : T.ink, borderColor: on ? T.primary : T.line })}>{"Stap " + (i + 1)}</button>;
      })}
      {onAdd && <button onClick={onAdd} style={Object.assign({}, btn, { padding: "5px 11px", fontSize: 12, borderStyle: "dashed", color: T.muted })}>+ Stap</button>}
    </div>
  );
}

// ---------------------------------------------------------------- legenda

// Alleen de tekens die echt in de tekening (over alle stappen) gebruikt worden,
// op dezelfde groene ondergrond als de baan zodat de kleuren precies kloppen.
var LEGEND = [
  { t: "p", label: "Speler" }, { t: "c", label: "Trainer" }, { t: "b", label: "Bal" },
  { t: "k", label: "Pion" }, { t: "r", label: "Looplijn" }, { t: "s", label: "Balbaan" },
];
function LegendIcon({ t }) {
  var s = { width: t === "r" || t === "s" ? 26 : 16, height: 16, display: "block", flex: "none" };
  if (t === "p" || t === "c") return <svg viewBox="0 0 16 16" style={s}><circle cx="8" cy="8" r="7" fill={t === "p" ? C.player : C.coach} stroke="#fff" strokeWidth="1.2" /><text x="8" y="11.2" textAnchor="middle" fontSize="8.5" fontWeight="800" fill="#fff" fontFamily="Archivo, system-ui, sans-serif">{t === "p" ? "1" : "T"}</text></svg>;
  if (t === "b") return <svg viewBox="0 0 16 16" style={s}><circle cx="8" cy="8" r="4" fill={C.ball} stroke="#4A4A10" strokeWidth="0.9" /></svg>;
  if (t === "k") return <svg viewBox="0 0 16 16" style={s}><path d="M8 2.5 L13.5 13 H2.5 Z" fill={C.cone} stroke="#fff" strokeWidth="1" strokeLinejoin="round" /></svg>;
  var run = t === "r";
  return (
    <svg viewBox="0 0 26 16" style={s}>
      <line x1="2" y1="8" x2="18" y2="8" stroke={run ? C.run : C.shot} strokeWidth="2.2" strokeDasharray={run ? "3.2 2.4" : undefined} />
      <path d="M17 3.5 L24 8 L17 12.5 Z" fill={run ? C.run : C.shot} />
    </svg>
  );
}
function Legend({ frames }) {
  var used = {};
  (frames || []).forEach(function (f) { (f.items || []).forEach(function (it) { used[it.t === "kr" ? "k" : it.t] = true; }); });
  var list = LEGEND.filter(function (x) { return used[x.t]; });
  if (!list.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", alignItems: "center", marginTop: 8, padding: "7px 12px", borderRadius: 10, background: C.surround, color: "#fff", fontSize: 12, fontWeight: 600, fontFamily: FF }}>
      {list.map(function (x) {
        return <span key={x.t} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><LegendIcon t={x.t} />{x.label}</span>;
      })}
    </div>
  );
}

// ---------------------------------------------------------------- bekijken

function DiagramViewer({ diagram, onEdit }) {
  var frames = diagram.frames;
  var [fi, setFi] = useState(0);
  var [playing, setPlaying] = useState(false);
  // De stap die hiervoor zichtbaar was: daar vandaan bewegen spelers en bal, en die
  // lijnen vervagen.
  var [prevFi, setPrevFi] = useState(null);
  var f = frames[Math.min(fi, frames.length - 1)];
  var prevFrame = prevFi === null ? null : frames[prevFi];

  function go(i) { setPrevFi(i === fi ? null : fi); setFi(i); }

  useEffect(function () {
    if (!playing) return;
    if (fi >= frames.length - 1) { setPlaying(false); return; }
    var t = setTimeout(function () { setPrevFi(fi); setFi(fi + 1); }, 2200);
    return function () { clearTimeout(t); };
  }, [playing, fi, frames.length]);

  function play() { setPrevFi(null); setFi(0); setPlaying(true); }

  return (
    <>
      <div style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", background: C.surround, borderRadius: 12, overflow: "hidden" }}>
        <CourtSvg frame={f} prevFrame={prevFrame} stepKey={fi + "-" + prevFi} animate style={{ height: "100%", maxHeight: "60dvh" }} />
      </div>
      <Legend frames={frames} />
      {f.note && <p style={{ fontSize: 14, lineHeight: 1.5, color: "#4A4740", marginTop: 10, whiteSpace: "pre-line" }}>{f.note}</p>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {frames.length > 1 && <button onClick={play} style={btn}>{playing ? "Bezig…" : "▶ Afspelen"}</button>}
          <StepTabs frames={frames} fi={fi} setFi={function (i) { setPlaying(false); go(i); }} />
        </div>
        {onEdit && <button onClick={onEdit} style={btn}>✎ Bewerken</button>}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- bewerken

function DiagramEditor({ initial, onSave, onCancel }) {
  var [frames, setFrames] = useState(function () { return cp(hasDiagram(initial) ? initial.frames : emptyDiagram().frames); });
  var [fi, setFi] = useState(0);
  var [tool, setTool] = useState("p");
  var [hist, setHist] = useState([]);
  var [drag, setDrag] = useState(null);
  var [sureDel, setSureDel] = useState(false);
  var svgRef = useRef(null);
  var f = frames[fi];

  function remember() { setHist(function (h) { return h.concat([cp(frames)]).slice(-60); }); }
  function undo() {
    if (!hist.length) return;
    var prev = hist[hist.length - 1];
    setHist(hist.slice(0, -1));
    setFrames(prev);
    if (fi > prev.length - 1) setFi(prev.length - 1);
  }
  function setItems(fn) {
    setFrames(function (fr) { var n = cp(fr); n[fi].items = fn(n[fi].items); return n; });
  }
  function toPt(e) {
    var svg = svgRef.current;
    var p = svg.createSVGPoint(); p.x = e.clientX; p.y = e.clientY;
    var q = p.matrixTransform(svg.getScreenCTM().inverse());
    return { x: clampX(q.x), y: clampY(q.y) };
  }
  function capture(e) { try { svgRef.current.setPointerCapture(e.pointerId); } catch (err) { /* niet erg */ } }

  function onBgDown(e) {
    var pt = toPt(e);
    if (tool === "p" || tool === "c" || tool === "b" || tool === "k") {
      remember();
      var it = { id: uid(), t: tool, x: pt.x, y: pt.y };
      if (tool === "p") {
        var max = 0; f.items.forEach(function (x) { if (x.t === "p") max = Math.max(max, parseInt(x.n, 10) || 0); });
        it.n = String(max + 1);
      }
      setItems(function (items) { return items.concat([it]); });
    } else if (tool === "r" || tool === "s" || tool === "kr") {
      capture(e);
      setDrag({ kind: "new", it: { id: uid(), t: tool, x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y } });
    }
  }
  function onItemDown(e, it, part) {
    if (tool !== "move" && tool !== "erase") return; // plaatsen gaat door naar de baan
    e.stopPropagation();
    if (tool === "erase") {
      remember();
      setItems(function (items) { return items.filter(function (x) { return x.id !== it.id; }); });
      return;
    }
    capture(e);
    setDrag({ kind: "move", id: it.id, part: part, start: toPt(e), orig: cp(it), before: cp(frames), moved: false });
  }
  function onMove(e) {
    if (!drag) return;
    var pt = toPt(e);
    if (drag.kind === "new") {
      setDrag(Object.assign({}, drag, { it: Object.assign({}, drag.it, { x2: pt.x, y2: pt.y }) }));
      return;
    }
    var dx = pt.x - drag.start.x, dy = pt.y - drag.start.y, o = drag.orig;
    if (!drag.moved && Math.abs(dx) + Math.abs(dy) > 0.05) setDrag(Object.assign({}, drag, { moved: true }));
    setItems(function (items) {
      return items.map(function (x) {
        if (x.id !== drag.id) return x;
        if (drag.part === "p") return Object.assign({}, x, { x: clampX(o.x + dx), y: clampY(o.y + dy) });
        if (drag.part === "a") return Object.assign({}, x, { x1: pt.x, y1: pt.y });
        if (drag.part === "b") return Object.assign({}, x, { x2: pt.x, y2: pt.y });
        return Object.assign({}, x, { x1: clampX(o.x1 + dx), y1: clampY(o.y1 + dy), x2: clampX(o.x2 + dx), y2: clampY(o.y2 + dy) });
      });
    });
  }
  function onUp() {
    if (!drag) return;
    if (drag.kind === "new") {
      var a = drag.it;
      if (Math.hypot(a.x2 - a.x1, a.y2 - a.y1) > 0.6) { remember(); setItems(function (items) { return items.concat([a]); }); }
    } else if (drag.moved) {
      var before = drag.before;
      setHist(function (h) { return h.concat([before]).slice(-60); });
    }
    setDrag(null);
  }

  // Nieuwe stap: zelfde spelers, ballen en pionnen op hun plek (dan kun je ze verschuiven),
  // zonder de pijlen van de vorige stap.
  function addStep() {
    remember();
    var n = cp(frames);
    n.splice(fi + 1, 0, { note: "", items: cp(f.items.filter(function (x) { return !isArrow(x); })) });
    setFrames(n); setFi(fi + 1);
  }
  function removeStep() {
    if (frames.length < 2) return;
    remember();
    var n = cp(frames); n.splice(fi, 1);
    setFrames(n); setFi(Math.max(0, fi - 1));
  }
  function setNote(v) { setFrames(function (fr) { var n = cp(fr); n[fi].note = v; return n; }); }
  function save() {
    var d = { frames: frames };
    onSave(hasDiagram(d) ? d : null);
  }

  return (
    <>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
        {TOOLS.map(function (t) {
          var on = tool === t.key;
          return (
            <button key={t.key} onClick={function () { setTool(t.key); }} title={t.label}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "5px 4px", minWidth: 46, borderRadius: 8, cursor: "pointer", fontFamily: FF, fontSize: 10, fontWeight: 700, border: "1px solid " + (on ? T.primary : T.line), background: on ? "#E9EDF5" : "#fff", color: T.ink }}>
              <ToolIcon k={t.key} />{t.label}
            </button>
          );
        })}
        <button onClick={undo} disabled={!hist.length} title="Ongedaan maken"
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "5px 4px", minWidth: 46, borderRadius: 8, cursor: hist.length ? "pointer" : "default", fontFamily: FF, fontSize: 10, fontWeight: 700, border: "1px solid " + T.line, background: "#fff", color: hist.length ? T.ink : "#C9C4BA" }}>
          <span style={{ fontSize: 16, lineHeight: "18px" }}>↶</span>Terug
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center", background: C.surround, borderRadius: 12, overflow: "hidden" }}>
        <CourtSvg frame={f} svgRef={svgRef} onBgDown={onBgDown} onItemDown={onItemDown} onMove={onMove} onUp={onUp}
          draft={drag && drag.kind === "new" ? drag.it : null} showHandles={tool === "move"}
          style={{ height: "100%", maxHeight: "56dvh", cursor: tool === "move" ? "default" : "crosshair" }} />
      </div>
      <Legend frames={frames} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <StepTabs frames={frames} fi={fi} setFi={setFi} onAdd={addStep} />
        {frames.length > 1 && <button onClick={removeStep} style={Object.assign({}, btn, { padding: "5px 10px", fontSize: 12, color: "#B83232" })}>Stap verwijderen</button>}
      </div>
      <textarea value={f.note || ""} onChange={function (e) { setNote(e.target.value); }} rows={2}
        placeholder={frames.length > 1 ? "Uitleg bij stap " + (fi + 1) + " (optioneel)" : "Korte uitleg bij de tekening (optioneel)"}
        style={{ width: "100%", marginTop: 8, border: "1px solid " + T.line, borderRadius: 8, padding: 8, fontSize: 13, lineHeight: 1.5, fontFamily: FF, resize: "vertical", outline: "none", color: "#4A4740" }} />
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        {hasDiagram(initial)
          ? <button onClick={function () { if (sureDel) onSave(null); else setSureDel(true); }} style={Object.assign({}, btn, { color: "#B83232" })}>{sureDel ? "Zeker? Klik nogmaals" : "Tekening verwijderen"}</button>
          : <span />}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onCancel} style={btn}>Annuleren</button>
          <button onClick={save} style={btnPrimary}>Opslaan</button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- pop-up + knopje

function DiagramDialog({ title, diagram, canEdit, onSave, onClose }) {
  var has = hasDiagram(diagram);
  var [mode, setMode] = useState(has ? "view" : "edit");

  // Toetsen afvangen zolang de pop-up open is (anders bladert bv. het weekoverzicht
  // met de pijltjestoetsen mee op de achtergrond).
  useEffect(function () {
    function k(e) {
      var tag = (e.target && e.target.tagName) || "";
      if (e.key === "Escape" && mode === "view") onClose();
      if (tag !== "TEXTAREA" && tag !== "INPUT") e.stopImmediatePropagation();
    }
    window.addEventListener("keydown", k, true);
    return function () { window.removeEventListener("keydown", k, true); };
  }, [mode, onClose]);

  function stop(e) { e.stopPropagation(); }

  // Via een portal direct op <body>: zo staat de pop-up altijd over het hele scherm,
  // ook binnen kaarten die met CSS-transforms verschuiven (weekoverzicht).
  return createPortal(
    <div onClick={mode === "view" ? onClose : undefined}
      onWheel={stop} onPointerDown={stop} onPointerMove={stop} onPointerUp={stop} onTouchStart={stop} onTouchMove={stop} onTouchEnd={stop} onMouseDown={stop}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 10 }}>
      <div onClick={function (e) { e.stopPropagation(); }}
        style={{ background: "#fff", borderRadius: 16, padding: 14, width: "100%", maxWidth: 560, height: "min(96dvh, 900px)", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.25)", fontFamily: FF }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: T.ink, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title || "Tekening"}</div>
          {mode === "view" && <button onClick={onClose} aria-label="Sluiten" style={{ border: "none", background: "none", fontSize: 22, lineHeight: 1, cursor: "pointer", color: T.muted, padding: 4 }}>×</button>}
        </div>
        {mode === "view"
          ? <DiagramViewer diagram={diagram} onEdit={canEdit ? function () { setMode("edit"); } : null} />
          : <DiagramEditor initial={diagram}
              onSave={function (d) { onSave(d); if (hasDiagram(d)) setMode("view"); else onClose(); }}
              onCancel={function () { if (has) setMode("view"); else onClose(); }} />}
      </div>
    </div>,
    document.body
  );
}

// Klein knopje naast een oefening. Zonder tekening alleen zichtbaar voor wie mag bewerken.
export function DiagramButton({ diagram, canEdit, onSave, title, style }) {
  var [open, setOpen] = useState(false);
  var has = hasDiagram(diagram);
  if (!has && !canEdit) return null;
  return (
    <>
      <button onClick={function (e) { e.stopPropagation(); setOpen(true); }} title={has ? "Tekening bekijken" : "Tekening maken"}
        style={Object.assign({ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid " + (has ? "#BCDDC6" : T.line), background: has ? "#EDF6EF" : "#fff", borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: has ? "#2F5E3A" : T.muted, cursor: "pointer", fontFamily: FF, whiteSpace: "nowrap", flex: "none" }, style)}>
        <CourtIcon />{has ? "Tekening" : "+ Tekening"}
      </button>
      {open && <DiagramDialog title={title} diagram={diagram} canEdit={canEdit} onSave={onSave} onClose={function () { setOpen(false); }} />}
    </>
  );
}
